import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Copy, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Badge } from "./Dashboard";

const LIST_FIELDS = [
  ["venues", "Locales"],
  ["events", "Eventos"],
  ["people_or_companies", "Personas / Empresas"],
  ["promoters_pr", "PR / Promotores"],
  ["tools_channels", "Herramientas / Canales"],
  ["major_events", "Grandes eventos (Fashion Week, F1…)"],
  ["operational_problems", "Problemas operativos"],
  ["business_opportunities", "Oportunidades de negocio"],
  ["competitors", "Competidores"],
  ["hypotheses_to_investigate", "Hipótesis a investigar"],
  ["follow_up_items", "Seguimientos"],
  ["sources", "Fuentes"],
  ["observations", "Observaciones"],
];

const TEXT_FIELDS = [
  ["vip_table_info", "Info mesas VIP"],
  ["guest_list_info", "Info lista de invitados"],
  ["bottle_service", "Servicio de botellas"],
  ["private_events", "Eventos privados"],
  ["afterparties", "Afterparties"],
  ["ncc_driver", "Conductor NCC"],
  ["security_hospitality", "Seguridad / Hospitality"],
];

function listToText(arr) {
  return (arr || []).map((i) => (i.classification === "INFERENCE" ? `${i.value} | INFERENCIA` : i.value)).join("\n");
}

function textToList(text) {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const parts = l.split("|").map((p) => p.trim());
      const tag = (parts[1] || "").toUpperCase();
      const classification = tag.startsWith("INF") ? "INFERENCE" : "FACT";
      return { value: parts[0], classification };
    });
}

const inputCls =
  "w-full bg-[#090a0f] border border-[#232838] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#8b5cf6] transition-colors";

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [form, setForm] = useState(null);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/observatory/reports/${id}`),
      api.get("/admin/audit", { params: { report_id: id, limit: 50 } }),
    ])
      .then(([r, a]) => {
        setReport(r.data);
        const f = { report_date: r.data.report_date, city: r.data.city || "" };
        LIST_FIELDS.forEach(([k]) => (f[k] = listToText(r.data[k])));
        TEXT_FIELDS.forEach(([k]) => (f[k] = r.data[k] || ""));
        setForm(f);
        setAudit(a.data.items);
      })
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { report_date: form.report_date, city: form.city };
      LIST_FIELDS.forEach(([k]) => (payload[k] = textToList(form[k])));
      TEXT_FIELDS.forEach(([k]) => (payload[k] = form[k]));
      const { data } = await api.put(`/observatory/reports/${id}`, payload);
      toast.success(data.status === "sin_cambios" ? "Sin cambios" : "Informe actualizado");
      const [r, a] = await Promise.all([
        api.get(`/observatory/reports/${id}`),
        api.get("/admin/audit", { params: { report_id: id, limit: 50 } }),
      ]);
      setReport(r.data);
      setAudit(a.data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div className="flex justify-center py-20" data-testid="detail-loading"><Loader2 className="w-6 h-6 animate-spin text-[#8b5cf6]" /></div>;
  if (!report)
    return <p className="text-sm text-red-400" data-testid="detail-not-found">Informe no encontrado.</p>;

  return (
    <div data-testid="report-detail-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button onClick={() => navigate("/informes")} data-testid="back-button" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> Volver al archivo
        </button>
        <button onClick={save} disabled={saving} data-testid="save-button"
          className="flex items-center gap-2 bg-[#8b5cf6] hover:bg-[#7c3aed] disabled:opacity-50 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar cambios
        </button>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight font-mono2">{report.report_date}</h1>
        <p className="text-xs text-slate-500 mt-1">
          {report.city} · extracción: {report.extraction_method === "llm" ? "automática (LLM)" : report.extraction_method} ·
          creado {new Date(report.created_at).toLocaleString("es-ES")} · editado {report.edit_count || 0} vez{(report.edit_count || 0) === 1 ? "" : "es"}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-4" data-testid="structured-pane">
          <div className="card-glow rounded-lg p-5 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-1.5">Fecha del informe</label>
              <input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })}
                data-testid="field-report-date" className={`${inputCls} [color-scheme:dark]`} />
            </div>
            <div>
              <label className="block text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-1.5">Ciudad</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="field-city" className={inputCls} />
            </div>
          </div>

          {LIST_FIELDS.map(([key, label]) => (
            <div key={key} className="card-glow rounded-lg p-4">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">{label}</label>
                <span className="text-[10px] text-slate-600">un elemento por línea · sufijo «| INFERENCIA»</span>
              </div>
              <textarea
                rows={Math.min(6, Math.max(2, (form[key] || "").split("\n").length))}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`field-${key}`}
                className={`${inputCls} font-mono2 text-xs resize-y`}
              />
              {(report[key] || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {report[key].map((it, i) => <Badge key={i} c={it.classification} />)}
                </div>
              )}
            </div>
          ))}

          {TEXT_FIELDS.map(([key, label]) => (
            <div key={key} className="card-glow rounded-lg p-4">
              <label className="block text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
              <textarea rows={2} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`field-${key}`} className={`${inputCls} resize-y`} />
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="card-glow rounded-lg p-5 lg:sticky lg:top-6" data-testid="original-pane">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-slate-500" />
                <h3 className="text-sm font-semibold">Informe original (inmutable)</h3>
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(report.full_report_text || ""); toast.success("Copiado"); }}
                data-testid="copy-original-button"
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
              >
                <Copy className="w-3.5 h-3.5" /> Copiar
              </button>
            </div>
            {report.full_report_text ? (
              <pre className="whitespace-pre-wrap font-mono2 text-xs text-slate-300 bg-[#090a0f] border border-[#232838] rounded-md p-4 max-h-[70vh] overflow-y-auto" data-testid="original-text">
                {report.full_report_text}
              </pre>
            ) : (
              <p className="text-xs text-slate-600" data-testid="original-empty">Este informe se creó solo con campos estructurados (sin texto original).</p>
            )}
          </div>

          <div className="card-glow rounded-lg p-5" data-testid="audit-pane">
            <h3 className="text-sm font-semibold mb-3">Historial de cambios</h3>
            {audit.length === 0 ? (
              <p className="text-xs text-slate-600">Sin registros.</p>
            ) : (
              <ul className="space-y-2">
                {audit.map((a) => (
                  <li key={a.id} className="text-xs border-l-2 border-[#3b4259] pl-3 py-1" data-testid={`audit-entry-${a.action}`}>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-300 font-medium">{a.action}</span>
                      <span className="font-mono2 text-slate-600">{new Date(a.timestamp).toLocaleString("es-ES")}</span>
                    </div>
                    <div className="text-slate-500">{a.actor}{a.note ? ` — ${a.note}` : ""}</div>
                    {Object.keys(a.changes || {}).length > 0 && (
                      <div className="text-slate-600 mt-0.5">Campos: {Object.keys(a.changes).join(", ")}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
